function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { sortNodes, isElement, getNextIndex, getPrevIndex } from './utils';
/**
 * @internal
 *
 * Class to manage descendants and their relative indices in the DOM.
 * It uses `node.compareDocumentPosition(...)` under the hood
 */
export class DescendantsManager {
  constructor() {
    _defineProperty(this, "descendants", void 0);
    this.descendants = new Map();
  }
  register(nodeOrOptions) {
    if (nodeOrOptions == null) return;
    if (isElement(nodeOrOptions)) {
      return this.registerNode(nodeOrOptions);
    }
    return node => {
      this.registerNode(node, nodeOrOptions);
    };
  }
  unregister(node) {
    this.descendants.delete(node);
    var sorted = sortNodes(Array.from(this.descendants.keys()));
    this.assignIndex(sorted);
  }
  destroy() {
    this.descendants.clear();
  }
  assignIndex(descendants) {
    this.descendants.forEach(descendant => {
      var index = descendants.indexOf(descendant.node);
      descendant.index = index;
      descendant.node.dataset.index = descendant.index.toString();
    });
  }
  count() {
    return this.descendants.size;
  }
  enabledCount() {
    return this.enabledValues().length;
  }
  values() {
    var values = Array.from(this.descendants.values());
    return values.sort((a, b) => a.index - b.index);
  }
  enabledValues() {
    return this.values().filter(descendant => !descendant.disabled);
  }
  item(index) {
    if (this.count() === 0) return undefined;
    return this.values()[index];
  }
  enabledItem(index) {
    if (this.enabledCount() === 0) return undefined;
    return this.enabledValues()[index];
  }
  first() {
    return this.item(0);
  }
  firstEnabled() {
    this.enabledItem(0);
  }
  last() {
    return this.item(this.descendants.size - 1);
  }
  lastEnabled() {
    var lastIndex = this.enabledValues().length - 1;
    return this.enabledItem(lastIndex);
  }
  indexOf(node) {
    var _this$descendants$get, _this$descendants$get2;
    if (!node) return -1;
    return (_this$descendants$get = (_this$descendants$get2 = this.descendants.get(node)) === null || _this$descendants$get2 === void 0 ? void 0 : _this$descendants$get2.index) !== null && _this$descendants$get !== void 0 ? _this$descendants$get : -1;
  }
  enabledIndexOf(node) {
    if (node == null) return -1;
    return this.enabledValues().findIndex(i => i.node.isSameNode(node));
  }
  next(index) {
    var loop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var next = getNextIndex(index, this.count(), loop);
    return this.item(next);
  }
  nextEnabled(index) {
    var loop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var item = this.item(index);
    if (!item) return;
    var enabledIndex = this.enabledIndexOf(item.node);
    var nextEnabledIndex = getNextIndex(enabledIndex, this.enabledCount(), loop);
    return this.enabledItem(nextEnabledIndex);
  }
  prev(index) {
    var loop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var prev = getPrevIndex(index, this.count() - 1, loop);
    return this.item(prev);
  }
  prevEnabled(index) {
    var loop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var item = this.item(index);
    if (!item) return;
    var enabledIndex = this.enabledIndexOf(item.node);
    var prevEnabledIndex = getPrevIndex(enabledIndex, this.enabledCount() - 1, loop);
    return this.enabledItem(prevEnabledIndex);
  }
  registerNode(node, options) {
    if (!node || this.descendants.has(node)) return;
    var keys = Array.from(this.descendants.keys()).concat(node);
    var sorted = sortNodes(keys);
    if (options !== null && options !== void 0 && options.disabled) {
      options.disabled = !!options.disabled;
    }
    var descendant = _objectSpread({
      node,
      index: -1
    }, options);
    this.descendants.set(node, descendant);
    this.assignIndex(sorted);
  }
}